import { installQualityTelemetry } from "@/telemetry/quality"
import "regenerator-runtime/runtime"
import React from "react"
import ReactDOM from "react-dom/client"
import { QueryClientProvider } from "react-query"
import { Provider } from "react-redux"

import "react-toastify/dist/ReactToastify.css"
import "./styles/toast-overrides.css"
import "./styles/interactive.css"
import store from "@/store"
import { MsalProvider } from "@azure/msal-react"
import { AuthenticationResult, EventMessage, EventType } from "@azure/msal-browser"
import { msalInstance } from "@/config/msalInstance"
import { queryClient } from "@/config/queryClient"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import DeploymentGate from "@/components/DeploymentGate/DeploymentGate"
import App from "@/App"
import { appInsights, updateAppInsightsAccount } from "@/telemetry/appInsights"
import { initI18n } from "./i18n"
import { t } from "i18next"
import { DateFormatProvider } from "@/contexts/DateFormatContext"
import { isIfcViewerNonFatalGlobalError } from "@/utils/ifcViewerErrorScope"
import { isChunkLoadError, reloadForStaleAssets } from "@/utils/chunkLoadError"

export { msalInstance, queryClient }

// Account management logic
const _handleAccountSelection = () => {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0])
        updateAppInsightsAccount(msalInstance)
    }
}

// Bootstrap MSAL before the first React render.
// MSAL Browser v3 requires initialize() to be awaited, and the B2C redirect
// callback must be processed via handleRedirectPromise() before any component
// (e.g. AutoLogin) can inspect the account state. Skipping this causes a login
// loop: the app renders unauthenticated and triggers loginRedirect() again
// before MSAL has finished consuming the auth response.
const _bootstrapMsal = async () => {
    await msalInstance.initialize()

    try {
        const redirectResult = await msalInstance.handleRedirectPromise()
        if (redirectResult?.account) {
            msalInstance.setActiveAccount(redirectResult.account)
            updateAppInsightsAccount(msalInstance)
        }
    } catch (error) {
        if (import.meta.env.DEV) {
            console.error("MSAL redirect handling failed:", error)
        }
    }

    // Fallback: adopt a cached account if the redirect did not return one.
    _handleAccountSelection()
}

// MSAL event callback with obfuscation-friendly structure
const _msalEventHandler = async (event: EventMessage) => {
    const { eventType, payload } = event

    if (eventType === EventType.LOGIN_SUCCESS && payload) {
        const authResult = payload as AuthenticationResult
        const account = authResult.account

        msalInstance.setActiveAccount(account)
        window.dispatchEvent(new CustomEvent("login-success"))
    } else if (eventType === EventType.LOGOUT_SUCCESS) {
        msalInstance.setActiveAccount(null)
    }
}

msalInstance.addEventCallback(_msalEventHandler)

// Error handling configuration
interface ErrorHandlerConfig {
    maxRetries: number
    retryTimeframe: number
    ignoredErrors: string[]
    excludedPaths: string[]
}

const _errorConfig: ErrorHandlerConfig = {
    maxRetries: 10,
    retryTimeframe: 30 * 60 * 1000, // 30 minutes
    ignoredErrors: ["ResizeObserver", "Cannot read properties of null (reading 'boundary')"],
    excludedPaths: ["/terms-of-service", "/privacy-policy"],
}

// Enhanced error handler
const _globalErrorHandler = (event: ErrorEvent) => {
    const currentPath = window.location.href.split("#")[1] || "/"

    // Skip error handling for excluded paths
    if (_errorConfig.excludedPaths.includes(currentPath)) {
        return null
    }

    if (!event || !event.message) {
        return
    }

    if (isIfcViewerNonFatalGlobalError(event, _errorConfig.ignoredErrors)) {
        const isKnownBenign = _errorConfig.ignoredErrors.some(substr => event.message.includes(substr))
        if (import.meta.env.DEV && !isKnownBenign) {
            console.warn("Ignored non-fatal error (no reload):", event.message, event.filename)
        }
        return
    }

    if (isChunkLoadError(event.error ?? event.message)) {
        event.preventDefault()
        reloadForStaleAssets()
        return
    }

    const exception = event.error instanceof Error ? event.error : new Error(event.message)
    appInsights.trackException({
        exception,
        properties: {
            context: "window.error",
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        },
    })

    if (import.meta.env.DEV) {
        console.error("Global error:", event, event.error)
    }

    const now = Date.now()
    const lastRetryTime = parseInt(sessionStorage.getItem("lastRetryTime") ?? "0", 10)
    const retryCount = parseInt(sessionStorage.getItem("retryCount") ?? "0", 10)

    // Retry logic with exponential backoff
    if (now - lastRetryTime < _errorConfig.retryTimeframe) {
        if (retryCount < _errorConfig.maxRetries) {
            sessionStorage.setItem("retryCount", String(retryCount + 1))
            sessionStorage.setItem("lastRetryTime", String(now))

            const delay = Math.min(retryCount * retryCount * 1000, 30000) // Max 30s delay
            setTimeout(() => {
                if (import.meta.env.DEV) {
                    console.log("Reloading after error")
                }
                window.location.reload()
            }, delay)
        } else if (import.meta.env.DEV) {
            console.log("Max retries reached within timeframe")
        }
    } else {
        sessionStorage.setItem("retryCount", "1")
        sessionStorage.setItem("lastRetryTime", String(now))
        window.location.reload()
    }
}

// Unhandled promise rejections (async errors not caught by a .catch())
const _unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    const reason = event.reason

    if (isChunkLoadError(reason)) {
        event.preventDefault()
        reloadForStaleAssets()
        return
    }

    const exception =
        reason instanceof Error ? reason : new Error(typeof reason === "string" ? reason : JSON.stringify(reason))
    appInsights.trackException({
        exception,
        properties: { context: "unhandledrejection" },
    })
}

installQualityTelemetry()

// Attach error handlers
window.addEventListener("error", _globalErrorHandler)
window.addEventListener("unhandledrejection", _unhandledRejectionHandler)

// Render application
const _renderApp = () => {
    const rootElement = document.getElementById("root")
    if (!rootElement) {
        throw new Error(t("Root element not found"))
    }

    const root = ReactDOM.createRoot(rootElement)

    root.render(
        <React.StrictMode>
            <DateFormatProvider>
                <DeploymentGate onBootstrap={_bootstrapMsal}>
                    <MsalProvider instance={msalInstance}>
                        <QueryClientProvider client={queryClient}>
                            <Provider store={store}>
                                <ErrorBoundary>
                                    <App />
                                </ErrorBoundary>
                            </Provider>
                            {/* ReactQueryDevtools removed — use browser React Query extension if needed */}
                        </QueryClientProvider>
                    </MsalProvider>
                </DeploymentGate>
            </DateFormatProvider>
        </React.StrictMode>
    )
}

// Await i18n initialization before rendering to prevent a flash of untranslated
// content. MSAL bootstrap is deferred until the backend health check passes so
// users are not sent through B2C login while the app is restarting.
initI18n()
    .catch(error => {
        if (import.meta.env.DEV) {
            console.error("i18n initialization failed:", error)
        }
    })
    .then(() => _renderApp())
